import {
  App,
  Button,
  Card,
  Empty,
  InputNumber,
  Select,
  Space,
  Switch,
  Table,
  Tag,
  Typography,
} from 'antd';
import { SaveOutlined } from '@ant-design/icons';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import dayjs from 'dayjs';
import { academicsApi, type MarkRow } from '@/api/academics';
import { setupApi } from '@/api/setup';
import { useAuthStore } from '@/store/auth';
import { PageHeader } from '@/shared/components/page-header';
import { extractError } from '@/shared/lib/error-message';

interface RowState {
  enrollmentId: number;
  registrationNumber: string | null;
  studentName: string | null;
  absent: boolean;
  theory: number | null;
  practical: number | null;
}

export function MarksEntryPage() {
  const { message } = App.useApp();
  const qc = useQueryClient();
  const canEdit = useAuthStore((s) => s.hasAuthority('ACADEMICS:EDIT'));
  const academicYearId = useAuthStore((s) => s.academicYearId);
  // Teacher scope — they can only see/save marks for their owned sections,
  // owned subjects, and the exam_schedules derived from those.
  // unbounded=true (Super Admin / Principal / etc.) → no filtering.
  const scope = useAuthStore((s) => s.user?.scope);
  const ownedSectionIds = scope && !scope.unbounded
    ? new Set(scope.teacherClassSectionIds)
    : null;
  const ownedScheduleIds = scope && !scope.unbounded
    ? new Set(scope.teacherExamScheduleIds)
    : null;

  const [classSectionId, setClassSectionId] = useState<number | undefined>(undefined);
  const [examTypeId, setExamTypeId] = useState<number | undefined>(undefined);
  const [examScheduleId, setExamScheduleId] = useState<number | undefined>(undefined);
  const [rows, setRows] = useState<RowState[]>([]);

  const { data: allSections } = useQuery({
    queryKey: ['setup', 'class-sections', academicYearId],
    queryFn: () => setupApi.listClassSections(academicYearId ?? undefined),
    staleTime: 60_000,
  });
  const sections = ownedSectionIds
    ? (allSections ?? []).filter((s) => ownedSectionIds.has(s.id))
    : allSections;

  const { data: examTypes } = useQuery({
    queryKey: ['setup', 'lookups', 'exam-types', 'active'],
    queryFn: () => setupApi.listLookup('exam-types', true),
    staleTime: 5 * 60_000,
  });

  const { data: allSchedules } = useQuery({
    queryKey: ['academics', 'exam-schedule', classSectionId, examTypeId],
    queryFn: () => academicsApi.examSchedule(classSectionId!, examTypeId),
    enabled: classSectionId != null && examTypeId != null,
  });
  // Teacher only sees exam schedules for subjects they actually teach in this
  // section — derived from scope.teacherExamScheduleIds populated at login.
  const schedules = ownedScheduleIds
    ? (allSchedules ?? []).filter((s) => ownedScheduleIds.has(s.id))
    : allSchedules;

  const { data: sheet, isFetching } = useQuery({
    queryKey: ['academics', 'marks', examScheduleId],
    queryFn: () => academicsApi.marksSheet(examScheduleId!),
    enabled: examScheduleId != null,
  });

  useEffect(() => {
    if (!sheet) {
      setRows([]);
      return;
    }
    setRows(
      sheet.rows.map((r: MarkRow) => ({
        enrollmentId: r.enrollmentId,
        registrationNumber: r.registrationNumber,
        studentName: r.studentName,
        absent: r.absent,
        theory: r.theoryMarks != null ? Number(r.theoryMarks) : null,
        practical: r.practicalMarks != null ? Number(r.practicalMarks) : null,
      })),
    );
  }, [sheet]);

  // Reset downstream selections when upstream changes
  useEffect(() => {
    setExamScheduleId(undefined);
  }, [classSectionId, examTypeId]);

  const save = useMutation({
    mutationFn: () =>
      academicsApi.saveMarks({
        examScheduleId: examScheduleId!,
        marks: rows.map((r) => ({
          enrollmentId: r.enrollmentId,
          absent: r.absent,
          theoryMarks: r.absent || r.theory == null ? null : r.theory.toFixed(2),
          practicalMarks: r.absent || r.practical == null ? null : r.practical.toFixed(2),
        })),
      }),
    onSuccess: () => {
      message.success('Marks saved');
      qc.invalidateQueries({ queryKey: ['academics', 'marks', examScheduleId] });
    },
    onError: (e) => message.error(extractError(e)),
  });

  const updateRow = (id: number, patch: Partial<RowState>) => {
    setRows((cur) => cur.map((r) => (r.enrollmentId === id ? { ...r, ...patch } : r)));
  };

  const theoryMax = sheet?.totalMarks ?? 100;
  const practicalMax = sheet?.practicalTotalMarks ?? 0;

  return (
    <div style={{ display: 'grid', gap: 20 }}>
      <PageHeader
        eyebrow="Module / Academics"
        title="Marks entry"
        description="Pick a section, exam type, and subject. Enter marks for the whole class on one screen."
        actions={
          canEdit && examScheduleId != null && rows.length > 0 ? (
            <Button
              type="primary"
              icon={<SaveOutlined />}
              loading={save.isPending}
              onClick={() => save.mutate()}
            >
              Save marks
            </Button>
          ) : null
        }
      />

      <Space wrap size={12}>
        <Select
          showSearch
          optionFilterProp="label"
          placeholder="Class section"
          style={{ minWidth: 320 }}
          value={classSectionId}
          onChange={(v) => setClassSectionId(v)}
          options={(sections ?? []).map((s) => ({
            value: s.id,
            label: `${s.className} · ${s.sectionName} · ${s.academicYearName}`,
          }))}
        />
        <Select
          showSearch
          optionFilterProp="label"
          placeholder="Exam type"
          style={{ minWidth: 200 }}
          value={examTypeId}
          onChange={(v) => setExamTypeId(v)}
          options={(examTypes ?? []).map((t) => ({ value: t.id, label: t.name }))}
        />
        <Select
          showSearch
          optionFilterProp="label"
          placeholder="Subject (scheduled)"
          style={{ minWidth: 280 }}
          value={examScheduleId}
          onChange={(v) => setExamScheduleId(v)}
          disabled={classSectionId == null || examTypeId == null}
          options={(schedules ?? []).map((s) => ({
            value: s.id,
            label: `${s.subjectName} · ${dayjs(s.examDate).format('D MMM')} · ${s.totalMarks} marks`,
          }))}
        />
      </Space>

      {examScheduleId == null ? (
        <Empty description="Pick a scheduled exam to enter marks" />
      ) : sheet ? (
        <Card>
          <Space wrap size={[12, 4]} style={{ marginBottom: 12 }}>
            <Typography.Text strong>{sheet.classSectionName}</Typography.Text>
            <Tag>{sheet.examTypeName}</Tag>
            <Tag color="processing">{sheet.subjectName}</Tag>
            <Typography.Text type="secondary">
              {dayjs(sheet.examDate).format('D MMM YYYY')} · Total {sheet.totalMarks} (pass {sheet.passingMarks})
            </Typography.Text>
            {sheet.hasPractical && (
              <Tag color="warning">
                + Practical {sheet.practicalTotalMarks} (pass {sheet.practicalPassingMarks})
              </Tag>
            )}
          </Space>

          <Table
            rowKey="enrollmentId"
            size="small"
            loading={isFetching}
            dataSource={rows}
            pagination={false}
            columns={[
              {
                title: '#',
                width: 50,
                render: (_, _row, idx) => idx + 1,
              },
              {
                title: 'Reg #',
                dataIndex: 'registrationNumber',
                width: 130,
              },
              {
                title: 'Student',
                dataIndex: 'studentName',
              },
              {
                title: 'Absent',
                width: 90,
                align: 'center',
                render: (_, row) => (
                  <Switch
                    size="small"
                    checked={row.absent}
                    disabled={!canEdit}
                    onChange={(v) => updateRow(row.enrollmentId, {
                      absent: v,
                      theory: v ? null : row.theory,
                      practical: v ? null : row.practical,
                    })}
                  />
                ),
              },
              {
                title: `Theory / ${theoryMax}`,
                width: 140,
                align: 'right',
                render: (_, row) => (
                  <InputNumber
                    size="small"
                    min={0}
                    max={theoryMax}
                    value={row.theory ?? undefined}
                    disabled={row.absent || !canEdit}
                    onChange={(v) => updateRow(row.enrollmentId, { theory: v == null ? null : Number(v) })}
                    style={{ width: '100%' }}
                  />
                ),
              },
              ...(sheet.hasPractical
                ? [
                    {
                      title: `Practical / ${practicalMax}`,
                      width: 140,
                      align: 'right' as const,
                      render: (_: unknown, row: RowState) => (
                        <InputNumber
                          size="small"
                          min={0}
                          max={practicalMax}
                          value={row.practical ?? undefined}
                          disabled={row.absent || !canEdit}
                          onChange={(v) =>
                            updateRow(row.enrollmentId, { practical: v == null ? null : Number(v) })
                          }
                          style={{ width: '100%' }}
                        />
                      ),
                    },
                  ]
                : []),
            ]}
          />
        </Card>
      ) : null}
    </div>
  );
}
